// Library Methods
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import { SelectChangeEvent } from "@mui/material/Select";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";

// Config
import { priorityLanguageMap } from "src/config/languages";

// Types
import { UpdateClinicSettingsType } from "src/services/clinics/types";

type ClinicLanguageDropdownType = {
  isDisabled: boolean;
  currentSettings: UpdateClinicSettingsType;
  handleUpdateSettings: (settings: UpdateClinicSettingsType) => void;
};

const ClinicLanguageDropdown = ({
  isDisabled,
  currentSettings,
  handleUpdateSettings,
}: ClinicLanguageDropdownType) => {
  const { t } = useTranslation();

  // sorted priority languages
  const clinicDropdownOptions = useMemo(
    () =>
      Object.entries(priorityLanguageMap)
        .map(([_, lang]) => ({ displayName: t(lang.label), value: lang.label }))
        .sort((a, b) => a?.displayName?.localeCompare(b?.displayName)),
    [t]
  );

  const handleSetClinicLanguage = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = clinicDropdownOptions.find(
        (option) => option.displayName === event.target.value
      )?.value;

      if (value) {
        handleUpdateSettings({
          ...currentSettings,
          clinicDefaultLanguage: value,
        });
      }
    },
    [clinicDropdownOptions, currentSettings, handleUpdateSettings]
  );

  return (
    <SelectDropdown
      id="clinic-language-dropdown"
      disabled={isDisabled}
      dropdownOptions={clinicDropdownOptions}
      value={t(currentSettings.clinicDefaultLanguage)}
      handleChange={handleSetClinicLanguage}
    />
  );
};

export default ClinicLanguageDropdown;
